﻿import React, { ForwardedRef, forwardRef, useEffect, useMemo, useState } from 'react';

import { GMetrixTable, GMetrixTableHandle } from 'components/GMetrixTable/GMetrixTable';
import { GMetrixTableColumnProps } from 'components/GMetrixTable/components';
import { CompositeFilterDescriptor, filterBy } from 'utils/dataQuery';
import { TransactionPermissions } from 'pages/access_codes/types';
import { FieldSettings, SortDescriptor } from 'utils/dataQuery';
import type { ReportPermissions } from 'pages/reports/types';
import { StudentPermissions } from 'pages/students/types';
import { Student, StudentApi } from 'apis/StudentApi';
import { GroupPermissions } from 'pages/groups/types';
import { UsersApi } from 'apis/UsersApi';
import { useIntl } from 'react-intl';

import 'components/StudentGmetrixTable/studentGmetrixTable.scss';

type StudentGmetrixTableProps = {
    advancedSearchFields: FieldSettings[];
    allowArchived?: boolean;
    allowExport?: boolean;
    archivedStudents?: boolean;
    columnData: GMetrixTableColumnProps<Student>[];
    defaultItemsPerPage?: number;
    initialSkip?: number;
    loadingGridData: boolean;
    manualLoadGridData?: boolean;
    manuallyRenderStudents?: boolean;
    multiSelect?: boolean;
    onDetailsItemClick?: (dataItem: Student) => void | undefined;
    onExportAllData?: () => Promise<Student[]>;
    onFilterChange?: (filters: { showArchived: boolean }) => void;
    onPreSearchResults?: (results: Student[]) => void;
    onSelectedChange?: (selected: Student[]) => void;
    onServerPageChange?: (skip: number, take: number) => void;
    onServerSortChange?: (sort: SortDescriptor[]) => void;
    permissions: TransactionPermissions | StudentPermissions | GroupPermissions | ReportPermissions;
    searchTerm: string;
    selectableRows?: boolean;
    selectedStudents?: Student[];
    serverSidePagination?: boolean;
    serverTotalCount?: number;
    setLoadingGridData: React.Dispatch<React.SetStateAction<boolean>>;
    setSearchTerm: (searchTerm: string) => void;
    setStudents: (students: Student[]) => void;
    students: Student[];
    toggleColumns?: boolean;
}

const StudentGmetrixTableInner = (props: StudentGmetrixTableProps, ref: ForwardedRef<GMetrixTableHandle>) => {

    const { advancedSearchFields, allowArchived = false, allowExport = true, archivedStudents, columnData,
        defaultItemsPerPage, initialSkip, loadingGridData, manuallyRenderStudents = false, multiSelect = true, onDetailsItemClick, onExportAllData,
        onFilterChange, onPreSearchResults, onSelectedChange, onServerPageChange, onServerSortChange, permissions,
        searchTerm, selectableRows = true, selectedStudents, serverSidePagination = false, serverTotalCount,
        setLoadingGridData, setSearchTerm, setStudents, students, toggleColumns = true } = props;

    const [advancedFilter, setAdvancedFilter] = useState<CompositeFilterDescriptor>({ filters: [], logic: 'and' });
    const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
    const [studentSwitch, setStudentSwitch] = useState(archivedStudents);
    const rowData = useMemo(() => serverSidePagination ? filteredStudents : filterBy(filteredStudents, advancedFilter), [filteredStudents, advancedFilter, serverSidePagination]);
    const { formatMessage } = useIntl();

    useEffect(() => {

        const myStudents = sessionStorage.getItem('myStudents');

        let initialShowArchived = archivedStudents ?? false;

        if (myStudents) {

            initialShowArchived = JSON.parse(myStudents);

        }

        setStudentSwitch(initialShowArchived);

        if (serverSidePagination && onFilterChange) {

            onFilterChange({ showArchived: initialShowArchived });

        }

        if (!manuallyRenderStudents && !serverSidePagination) {

            setLoadingGridData(false);

        }

    }, []);

    useEffect(() => {

        if (serverSidePagination) {

            setFilteredStudents(students);
            return;

        }

        if (studentSwitch) {

            // Showing ALL students whether they're archived or not.
            setFilteredStudents(students);

        } else {

            const nonArchiveStudentList = students.filter(student => !student.Archived);
            setFilteredStudents(nonArchiveStudentList);

        }

    }, [studentSwitch, students]);

    const handleArchivedChange = (checked: boolean) => {

        setStudentSwitch(checked);
        sessionStorage.setItem('myStudents', JSON.stringify(checked));
        UsersApi.SetPagePreference(
            'http://schemas.gmetrix.net/claims/Admin/Preference/PagePreference/ArchivedStudents',
            checked ? 'Show' : 'Hide'
        );
        if (serverSidePagination && onFilterChange) {

            onFilterChange({ showArchived: checked });

        }

    };

    return (
        <>
            <GMetrixTable
                filters={allowArchived ? [
                    {
                        checked: studentSwitch,
                        label: formatMessage({ id: 'show_archived_student.48725600', defaultMessage: 'Show Archived Student' }),
                        onSelect: handleArchivedChange,
                        type: 'checkbox'
                    }
                ] : undefined}
                allowExport={allowExport}
                bottomBarTableName={formatMessage({ id: 'students.c6d199ef', defaultMessage: 'Students', description: 'menu' })}
                columnData={columnData}
                defaultItemsPerPage={defaultItemsPerPage}
                excelExportFileName={formatMessage({ id: 'studentlist.7a112621', defaultMessage: 'StudentList' })}
                initialSkip={initialSkip}
                loading={loadingGridData}
                multiSelect={multiSelect}
                noDataMessage={formatMessage({ id: 'search_for_a_student_to_begin_using.1f510baf', defaultMessage: 'Search for a student to begin using' })}
                onDetailsItemClick={onDetailsItemClick}
                onExportAllData={onExportAllData}
                onPreSearchResults={onPreSearchResults}
                onSelectedChange={onSelectedChange}
                onServerPageChange={serverSidePagination ? onServerPageChange : undefined}
                onServerSortChange={serverSidePagination ? onServerSortChange : undefined}
                ref={ref}
                resetPageSignal={`archived:${studentSwitch}`}
                rowData={rowData}
                searchPlaceholder={formatMessage({ id: 'search_by_name_email_access_code.b8245f31', defaultMessage: 'Search by name, email, access code...' })}
                searchTerm={searchTerm}
                selectableRows={selectableRows}
                selected={selectedStudents}
                serverSidePagination={serverSidePagination}
                serverTotalCount={serverTotalCount}
                setSearchTerm={setSearchTerm}
                tableName={formatMessage({ id: 'students.0933e5a0', defaultMessage: 'Students' })}
                toggleColumns={toggleColumns}
            />

        </>
    );

};

export const StudentGmetrixTable = forwardRef(StudentGmetrixTableInner);
